const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Admin Service - Handles all admin API calls
 */

// Get auth token from localStorage
const getAuthToken = () => {
    return localStorage.getItem('adminToken');
};

// Set auth token in localStorage
const setAuthToken = (token) => {
    localStorage.setItem('adminToken', token);
};

// Remove auth token
const removeAuthToken = () => {
    localStorage.removeItem('adminToken');
};

// Get auth headers
const getAuthHeaders = () => {
    const token = getAuthToken();
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

/**
 * Login with password
 */
export const login = async (password) => {
    const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Login failed');
    }

    // Store token
    setAuthToken(data.token);

    return data;
};

/**
 * Verify current token
 */
export const verifyToken = async () => {
    const response = await fetch(`${API_URL}/admin/verify`, {
        method: 'POST',
        headers: getAuthHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
        removeAuthToken();
        throw new Error(data.message || 'Token invalid');
    }

    return data;
};

/**
 * Logout
 */
export const logout = () => {
    removeAuthToken();
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
    return !!getAuthToken();
};

/**
 * Get all submissions
 */
export const getSubmissions = async () => {
    const response = await fetch(`${API_URL}/admin/submissions`, {
        headers: getAuthHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch submissions');
    }

    return data.submissions;
};

/**
 * Update submission status
 */
export const updateSubmissionStatus = async (rowNumber, status) => {
    const response = await fetch(`${API_URL}/admin/submissions/${rowNumber}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status })
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to update status');
    }

    return data;
};

/**
 * Get all coupons
 */
export const getCoupons = async () => {
    const response = await fetch(`${API_URL}/admin/coupons`, {
        headers: getAuthHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch coupons');
    }

    return data.coupons;
};

/**
 * Create new coupon
 */
export const createCoupon = async (couponData) => {
    const response = await fetch(`${API_URL}/admin/coupons`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(couponData)
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to create coupon');
    }

    return data.coupon;
};

/**
 * Update coupon
 */
export const updateCoupon = async (code, updates) => {
    const response = await fetch(`${API_URL}/admin/coupons/${code}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to update coupon');
    }

    return data.coupon;
};

/**
 * Delete coupon
 */
export const deleteCoupon = async (code) => {
    const response = await fetch(`${API_URL}/admin/coupons/${code}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to delete coupon');
    }

    return data;
};
